import React, { FC, useContext, useEffect, useState } from 'react';

import { Paper } from '@material-ui/core';
import Zoom from '@material-ui/core/Zoom';
import EditIcon from '@material-ui/icons/Edit';

import { GlobalContext } from '../../../../../../store';
import { IClubSettings } from '../../../../../../store/admin/clubs/actions';
import { ICourse } from '../../../../../../store/admin/courses/store';
import { ISource, IStripeToken } from '../../../../../../store/user/store';
import { IAdminClub } from '../../../../../../store/admin/clubs/store';

import Select from '../../../../../../components/AutoComplete';
import Button from '../../../../../../components/Button';
import { FormConsumer, FormProvider, IFormAttributes } from '../../../../../../components/Forms';
import Input from '../../../../../../components/Input';
import PaymentManager from '../../../../../../components/PaymentManager';
import Tooltip from '../../../../../../components/Tooltip';
import Switch from '../../../../../../components/YesNoSwitch';
import { useRouteInfoState } from '../../../../../../components/RouteInfo/Context/RouteInfo.context';

interface ICourseOption {
  address: string;
  label: string;
  value: string;
}

const Settings: FC = () => {
  const {
    actions,
    actions: {
      notifications: { pushSuccess, pushError }
    },
    adminClubCreditCard,
    adminClubs,
    adminCourses: courses,
    user
  } = useContext(GlobalContext);

  const { clubId } = useRouteInfoState();

  const [formCourse, setFormCourse] = useState({
    address: '',
    id: 0,
    name: ''
  });

  const [coursesOptions, setCoursesOptions] = useState<ICourseOption[]>([]);

  const [currentClub, setCurrentClub] = useState<IAdminClub>();

  const [formSettingsDisabled, setFormSettingsDisabled] = useState(true);

  useEffect(() => {
    if (courses.length > 0) {
      const newCoursesOptions = courses.map((item: ICourse) => ({
        address: item.address,
        label: item.name,
        value: String(item.id)
      }));

      setCoursesOptions(newCoursesOptions);
    }
  }, [courses]);

  const next = async (_: number, payment: IStripeToken | ISource): Promise<any> => {
    const response = await actions.adminClubCreditCard.update(clubId, payment);

    if (response.errors) {
      await pushError(response.errors.adminClubCreditCard.msg);
    } else {
      await actions.adminClubCreditCard.get(clubId);

      await pushSuccess('Credit card updated successfully!');
    }
  };

  useEffect(() => {
    if (clubId) {
      setCurrentClub(adminClubs[Number(clubId)]);
    }
  }, [adminClubs, clubId]);

  const onCourseChange = () => (event: any): void => {
    setFormCourse({
      address: event.address,
      id: event.value,
      name: event.label
    });
  };

  const onSubmitBasicSettings = async (data: any) => {
    const clubCourse: ICourse =
      formCourse.id !== 0 && formCourse.address !== '' && formCourse.name !== ''
        ? formCourse
        : data.course;

    const body: IClubSettings = {
      course: clubCourse.id,
      email: data.email,
      membershipPrice: data.membershipPrice,
      name: data.name,
      processPayment: data.processPayment
    };

    const response = await actions.adminClubs.edit(clubId, body);

    if (response.errors) {
      await pushError(response.errors.adminClubs.msg);
    } else {
      await pushSuccess('Club settings updated successfully!');
    }

    setFormSettingsDisabled(!formSettingsDisabled);
  };

  const toggleForm = () => {
    setFormSettingsDisabled(!formSettingsDisabled);
  };

  return !!currentClub ? (
    <Paper elevation={1} className="paper" id="basic-settings-paper">
      <div className="header-card-container header-card-container--settings">
        <span className="header-title">SETTINGS</span>
      </div>

      <div className="club-settings-forms">
        <FormProvider
          defaultValues={currentClub}
          onSubmit={onSubmitBasicSettings}
          validations={{
            email: ['required', 'email'],
            membershipPrice: 'currency'
          }}
        >
          <FormConsumer>
            {({ values, formActions: { setValue, toggleValue }, errors }: IFormAttributes) => (
              <div className="main-form">
                <div className="buttons-toggle-zoom">
                  <Zoom in={formSettingsDisabled}>
                    <Button
                      onClick={toggleForm}
                      variant="text"
                      color="primary"
                      className="button edit-club-settings-button"
                    >
                      <div className="icon-container">
                        <EditIcon className="edit-card-icon" />
                      </div>
                      EDIT
                    </Button>
                  </Zoom>

                  <Zoom in={!formSettingsDisabled}>
                    <div hidden={formSettingsDisabled} className="about-buttons-container">
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className="button save-settings-button"
                      >
                        SAVE
                      </Button>

                      <Button
                        onClick={toggleForm}
                        variant="text"
                        color="primary"
                        className="button cancel-settings-edition"
                      >
                        CANCEL
                      </Button>
                    </div>
                  </Zoom>
                </div>

                <div className="basic-settings-form-container">
                  <Input
                    label="CLUB NAME"
                    id="club-name"
                    type="text"
                    defaultValue={values.name}
                    onChange={setValue('name')}
                    hasError={errors.name}
                    disabled={formSettingsDisabled}
                    errorMessage="You must provide a valid name"
                    fullWidth={true}
                  />

                  <div className="course-selector-container">
                    <div className="course-title">
                      <div>HOME COURSE</div>
                    </div>

                    <Select
                      defaultValue={
                        {
                          address: ((values || {}).course || {}).address || '',
                          label: ((values || {}).course || {}).name || '',
                          value: String(((values || {}).course || {}).id) || ''
                        } as any
                      }
                      className="course-title"
                      isDisabled={formSettingsDisabled}
                      onChange={onCourseChange()}
                      options={coursesOptions}
                    />
                  </div>

                  <Input
                    className={`course-address ${
                      formSettingsDisabled ? 'course-address-disabled' : 'course-address-enabled'
                    }`}
                    label="COURSE ADDRESS"
                    id="course-address"
                    type="text"
                    value={
                      formCourse.address !== ''
                        ? formCourse.address
                        : ((values || {}).course || {}).address || ''
                    }
                    onChange={setValue('courseAddress')}
                    hasError={errors.courseAddress}
                    multiline={true}
                    rowsMax={3}
                    disabled={true}
                    errorMessage="Course Address cannot be empty"
                    fullWidth={true}
                  />

                  <Input
                    label="CLUB EMAIL ADDRESS"
                    id="club-email"
                    type="email"
                    defaultValue={values.email}
                    onChange={setValue('email')}
                    hasError={errors.email}
                    disabled={formSettingsDisabled}
                    errorMessage="You must provide a valid email"
                    fullWidth={true}
                  />
                </div>

                <div className="club-fees-form-container">
                  <div className="edit-club form">
                    <div className="fees-header">
                      <p className="fees-title">CLUB MEMBERSHIP FEES</p>

                      <Tooltip
                        classes={{ fab: 'tooltip-fab--shift-top-2' }}
                        sectionKey="CLUB_SETTINGS.MEMBERSHIP_FEES"
                      />
                    </div>

                    <div className="fees-switch">
                      <div className="fees-switch-left">
                        <p className="fees-switch-title">Will there be fees to join this club?</p>

                        <p className="fees-switch-subtitle">
                          Club fees are subject to WAGL’s $3.00 per player fee and any credit card
                          processer fees.
                        </p>
                      </div>

                      <div className="fees-switch-right">
                        <Switch
                          disabled={formSettingsDisabled}
                          value={values.processPayment}
                          checked={values.processPayment}
                          onChange={toggleValue('processPayment')}
                          color="primary"
                        />
                      </div>
                    </div>

                    <div className="membership-price">
                      <div
                        style={{
                          display: values.processPayment === true ? 'flex' : 'none'
                        }}
                        className="membership-price-left"
                      >
                        <p className="membership-price-title">How much are membership fees?</p>
                        <Input
                          label=""
                          id="membership-price-input"
                          type="text"
                          defaultValue={values.membershipPrice}
                          onChange={setValue('membershipPrice')}
                          hasError={errors.FORM}
                          disabled={formSettingsDisabled}
                          errorMessage="Membership price must have a valid format"
                          fullWidth={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </FormConsumer>
        </FormProvider>

        <div className="payment-method-container">
          <PaymentManager
            actions={actions}
            clubId={clubId}
            next={next}
            nextButton="Add card"
            selectDisabled={true}
            sources={adminClubCreditCard}
            textButton="ADD A NEW CREDIT CARD"
            user={user}
          />
        </div>
      </div>
    </Paper>
  ) : (
    <></>
  );
};

export default Settings;
